import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PlayCircle } from "lucide-react";
import { api } from "@/lib/api";
import { SECTIONS } from "@/lib/sections";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "long", day: "numeric", year: "numeric",
    });
  } catch { return ""; }
};

const sectionLabel = (slug) => {
  const found = SECTIONS.find((s) => s.slug === slug);
  return found ? found.label : (slug || "Broadcast");
};

/**
 * A "magazine cover" hero card that displays either the editor-featured
 * broadcast (via the ⭐ toggle on Live.jsx archive cards) or, as a fallback,
 * the newest ended broadcast. Renders nothing when there are no broadcasts.
 * Placed on the public Landing page just below the "Step Inside" CTA.
 */
export const NowPlayingCard = () => {
  const [broadcast, setBroadcast] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/live/featured");
        if (!cancelled) setBroadcast(data || null);
      } catch {
        if (!cancelled) setBroadcast(null);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!loaded || !broadcast) return null;

  const thumb = broadcast.custom_thumbnail_url
    || (broadcast.asset_playback_id
        ? `https://image.mux.com/${broadcast.asset_playback_id}/thumbnail.jpg?width=1200&time=${broadcast.thumbnail_time || 2}`
        : null);

  const editorName = broadcast.editor?.display_name || broadcast.editor?.username || "Le Salon";

  return (
    <section
      data-testid="now-playing-section"
      style={{ paddingTop: 64, paddingBottom: 32 }}
    >
      <div className="container-belle">
        <div style={{ maxWidth: 640, marginBottom: 28 }}>
          <div className="overline" style={{ color: "var(--brand-accent)" }}>
            Now playing · {sectionLabel(broadcast.section)}
          </div>
          <h2
            className="editorial-h1"
            style={{ fontSize: "clamp(28px, 4vw, 44px)", marginTop: 12, lineHeight: 1.15 }}
          >
            This week at the salon.
          </h2>
        </div>

        <Link
          to={`/broadcast/${broadcast.id}`}
          data-testid="now-playing-card"
          className="np-card"
          style={{
            display: "grid",
            gridTemplateColumns: "1.35fr 1fr",
            gap: 40,
            textDecoration: "none",
            color: "inherit",
            alignItems: "center",
          }}
        >
          {/* Thumbnail with play overlay */}
          <div
            className="np-thumb"
            style={{
              position: "relative",
              aspectRatio: "16 / 9",
              background: "#1a1a1a",
              overflow: "hidden",
            }}
          >
            {thumb ? (
              <img
                loading="lazy" decoding="async" src={thumb}
                alt={broadcast.title || "Broadcast"}
                style={{
                  width: "100%", height: "100%",
                  objectFit: "cover", display: "block",
                  transition: "transform 0.6s ease",
                }}
              />
            ) : (
              <div style={{
                width: "100%", height: "100%",
                display: "grid", placeItems: "center",
                background: "linear-gradient(135deg, #2a1c1e, #1a1a1a)",
              }} />
            )}
            {/* Play button overlay */}
            <div
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                display: "grid",
                placeItems: "center",
                background: "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.35) 100%)",
              }}
            >
              <div
                style={{
                  width: 84, height: 84, borderRadius: "50%",
                  background: "rgba(250, 249, 245, 0.94)",
                  display: "grid", placeItems: "center",
                  color: "var(--brand-accent)",
                  boxShadow: "0 12px 32px rgba(0,0,0,0.35)",
                }}
              >
                <PlayCircle size={44} strokeWidth={1.4} />
              </div>
            </div>
          </div>

          {/* Right column: title + meta */}
          <div>
            <div
              className="overline"
              style={{ color: "var(--brand-accent)", fontSize: 11 }}
            >
              {broadcast.editor?.is_editor ? "Editor's broadcast" : "A broadcast"} · {fmtDate(broadcast.ended_at)}
            </div>
            <h3
              className="font-serif"
              style={{
                fontSize: "clamp(28px, 3.4vw, 40px)",
                lineHeight: 1.15,
                marginTop: 14,
                marginBottom: 18,
                fontWeight: 400,
              }}
            >
              {broadcast.title || "A quiet broadcast"}
            </h3>
            <p
              className="editorial-body"
              style={{ fontSize: 15, color: "var(--text-muted)", marginBottom: 22 }}
            >
              A film from {editorName}. Press play to step in — no account required for this one.
            </p>
            <span
              className="link-underline"
              style={{
                fontSize: 12, letterSpacing: "0.2em",
                textTransform: "uppercase", fontWeight: 500,
              }}
            >
              Watch the broadcast →
            </span>
          </div>
        </Link>
      </div>

      <style>{`
        .np-card:hover .np-thumb img { transform: scale(1.03); }
        @media (max-width: 720px) {
          .np-card { grid-template-columns: 1fr !important; gap: 24px !important; }
        }
      `}</style>
    </section>
  );
};

export default NowPlayingCard;
